import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ListFinancialTransactions200 } from "@/api/financial";
import {
	listFinancialTransactionsQueryOptions,
	useCreateManualEntry,
} from "@/api/financial";
import { useMediaUploader } from "@/api/media";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ComprovanteAttachField } from "../-components/ComprovanteAttachField";
import {
	ManualEntryForm,
	type ManualEntryFormValues,
} from "../-components/ManualEntryForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/transactions/new",
)({
	component: NewManualEntryPage,
});

type TransactionRow = ListFinancialTransactions200["data"][number];

/** Map the most-recent MANUAL row to prefilled form defaults (date stays today). */
function toFormDefaults(row: TransactionRow): Partial<ManualEntryFormValues> {
	return {
		direction: row.direction,
		amount: row.amount,
		categoryId: row.categoryId,
		establishmentId: row.establishmentId ?? undefined,
		vendorId: row.vendorId ?? "",
		party: row.party ?? "",
		notes: row.notes ?? "",
		herdId: row.dimensions.herdId ?? "",
		animalId: row.dimensions.animalId ?? "",
		paddockId: row.dimensions.paddockId ?? "",
		equipmentId: row.dimensions.equipmentId ?? "",
		taskId: row.dimensions.taskId ?? "",
	};
}

function NewManualEntryPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const createMutation = useCreateManualEntry();
	const { uploadFile } = useMediaUploader();
	const [pendingAttachment, setPendingAttachment] = useState<File | null>(null);
	const [defaults, setDefaults] = useState<
		Partial<ManualEntryFormValues> | undefined
	>();
	// Bump to remount the form so react-hook-form re-reads the new defaults.
	const [formKey, setFormKey] = useState(0);

	// Newest rows first; the most recent MANUAL one seeds "Duplicar último".
	const { data: recent } = useQuery(
		listFinancialTransactionsQueryOptions(orgId, {
			limit: "20",
			sortBy: "createdAt",
			sortOrder: "desc",
		}),
	);
	const lastManual = recent?.data.find((row) => row.entryOrigin === "MANUAL");

	const duplicateLast = () => {
		if (!lastManual) return;
		setDefaults(toFormDefaults(lastManual));
		setFormKey((key) => key + 1);
	};

	const goToList = () =>
		navigate({
			to: "/app/org/$orgId/financial/transactions",
			params: { orgId },
		});

	// The transaction id only exists after the create succeeds, so the pending
	// comprovante is uploaded here. A failed upload never blocks the (already
	// persisted) entry — it can still be attached later from the detail page.
	const handleSubmit = async (
		data: Parameters<typeof createMutation.mutateAsync>[0]["data"],
	) => {
		const created = await createMutation.mutateAsync({
			organizationId: orgId,
			data,
		});
		if (pendingAttachment) {
			try {
				await uploadFile({
					orgId,
					entityType: "FinancialHubTransaction",
					entityId: created.id,
					file: pendingAttachment,
				});
			} catch {
				toast.error(t("financial.transactions.attachments.uploadFailed"));
			}
		}
		goToList();
	};

	return (
		<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
			<PageHeader
				title={t("financial.transactions.manualEntry.title")}
				showBackButton
			/>
			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					<div className="mb-2 flex justify-end">
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={duplicateLast}
							disabled={!lastManual}
						>
							<Copy className="mr-2 h-4 w-4" />
							{t("financial.transactions.manualEntry.duplicateLast")}
						</Button>
					</div>
					<ManualEntryForm
						key={formKey}
						orgId={orgId}
						defaultValues={defaults}
						isSubmitting={createMutation.isPending}
						onSubmit={handleSubmit}
						onCancel={goToList}
						attachmentSlot={
							<ComprovanteAttachField
								value={pendingAttachment}
								onChange={setPendingAttachment}
								disabled={createMutation.isPending}
							/>
						}
					/>
				</div>
			</div>
		</div>
	);
}
